import { zodResolver } from '@hookform/resolvers/zod';
import { UploadSimple, UsersThree } from '@phosphor-icons/react';
import {
  getGetPersonaQueryKey,
  getListPersonasQueryKey,
  useCreatePersonaAvatarUpload,
  useGetPersona,
  useSetPersonaAvatar,
  useUpdatePersona,
} from '@reelplicate/api-client/personas';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Avatar,
  Button,
  EmptyState,
  Field,
  IconButton,
  Skeleton,
  Stack,
  TextArea,
  TextInput,
  toast,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link as RouterLink, useParams } from 'react-router';

import {
  SettingsCard,
  SettingsPageLayout,
  SettingsSection,
} from '@/features/settings/settings-layout';
import { IMAGE_TYPES, imageProblem, type ImageType, putToSignedUrl } from '@/lib/upload';

import { PersonaAccounts } from './persona-accounts';
import { PersonaPosts } from './persona-posts';
import { ReferenceLook } from './reference-look';
import { PersonaSchema, type PersonaValues, toValues } from './schema';
import { PersonaTimezoneField } from './timezone-field';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * One persona (Figma 435:3338): who they are and how they sound, the
 * accounts they post from, every post found on those accounts with its
 * numbers per platform, and their reference look - the photos image
 * generation keeps them consistent with.
 */
export function PersonaPage() {
  const { personaId = '' } = useParams();
  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.[0];
  const workspaceId = workspace?.id ?? '';
  const { data: persona, error } = useGetPersona(workspaceId, personaId, {
    query: { enabled: Boolean(workspace && personaId), retry: false },
  });
  const queryClient = useQueryClient();
  const update = useUpdatePersona();
  const createAvatarUpload = useCreatePersonaAvatarUpload();
  const setAvatar = useSetPersonaAvatar();
  const avatarInput = useRef<HTMLInputElement>(null);
  const form = useForm<PersonaValues>({
    resolver: zodResolver(PersonaSchema),
    defaultValues: toValues(),
  });

  useEffect(() => {
    if (persona && !form.formState.isDirty) form.reset(toValues(persona));
  }, [persona, form]);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getListPersonasQueryKey(workspaceId) }),
      queryClient.invalidateQueries({
        queryKey: getGetPersonaQueryKey(workspaceId, personaId),
      }),
    ]);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const saved = await update.mutateAsync({
        id: workspaceId,
        personaId,
        data: values,
      });
      form.reset(toValues(saved));
    } catch {
      return; // Shown as a toast.
    }
    await refresh();
    toast.success('Persona saved');
  });

  const changeAvatar = async (file: File) => {
    const problem = await imageProblem(file, { what: 'avatar', minPx: 128 });
    if (problem) {
      toast.error(problem);
      return;
    }
    try {
      const { url, key } = await createAvatarUpload.mutateAsync({
        id: workspaceId,
        personaId,
        data: { contentType: file.type as ImageType, size: file.size },
      });
      await putToSignedUrl(url, file);
      await setAvatar.mutateAsync({ id: workspaceId, personaId, data: { key } });
    } catch (e) {
      if (e instanceof Error && e.message.startsWith('Upload failed')) {
        toast.error('The avatar didn’t upload. Try again.');
      }
      return;
    }
    await refresh();
  };

  if (error) {
    return (
      <EmptyState
        className="mt-24"
        icon={UsersThree}
        title="That persona isn’t here"
        description="It may have been deleted."
        actions={
          <Button asChild variant="outline">
            <RouterLink to="/personas">Back to personas</RouterLink>
          </Button>
        }
      />
    );
  }
  if (!persona || !workspace) {
    return (
      <SettingsPageLayout
        title="Persona"
        description=""
        back={{ to: '/personas', label: 'Personas' }}
      >
        <Skeleton className="h-96" shape="rectangle" />
      </SettingsPageLayout>
    );
  }

  const meta = [
    persona.shortName,
    plural(persona.accounts.length, 'account'),
    `reference look ${persona.referenceCount} of 5`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <SettingsPageLayout
      title={persona.name}
      description={meta}
      back={{ to: '/personas', label: 'Personas' }}
      onSubmit={(event) => void onSubmit(event)}
      leading={
        <div className="relative">
          <Avatar
            size={56}
            name={persona.name}
            src={persona.avatarUrl ?? undefined}
            alt=""
          />
          <input
            ref={avatarInput}
            type="file"
            accept={IMAGE_TYPES.join(',')}
            className="sr-only"
            tabIndex={-1}
            aria-label="Avatar file"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void changeAvatar(file);
            }}
          />
          {workspace.uploadsEnabled && (
            <IconButton
              type="button"
              icon={UploadSimple}
              size="sm"
              variant="secondary"
              aria-label="Change avatar"
              className="absolute -right-2 -bottom-2"
              onClick={() => avatarInput.current?.click()}
            />
          )}
        </div>
      }
      actions={
        <Button
          type="submit"
          disabled={!form.formState.isDirty}
          loading={update.isPending}
        >
          Save changes
        </Button>
      }
    >
      <SettingsSection
        title="Profile"
        description="How the persona is named, how they sound in captions and voiceovers, and the time zone their posts are scheduled in."
      >
        <SettingsCard>
          <Stack gap={4} className="py-5">
            <Stack gap={4} className="sm:flex-row sm:items-start">
              <Controller
                name="name"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field
                    label="Name"
                    error={fieldState.error?.message}
                    className="flex-1"
                  >
                    <TextInput {...field} />
                  </Field>
                )}
              />
              <Controller
                name="shortName"
                control={form.control}
                render={({ field, fieldState }) => (
                  <Field
                    label="Short name"
                    optional
                    helper="What captions and the calendar call them."
                    error={fieldState.error?.message}
                    className="flex-1"
                  >
                    <TextInput {...field} />
                  </Field>
                )}
              />
            </Stack>
            <Controller
              name="voice"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field
                  label="Voice"
                  optional
                  helper="Who they are and how they talk."
                  error={fieldState.error?.message}
                >
                  <TextArea {...field} rows={4} />
                </Field>
              )}
            />
            <Controller
              name="timezone"
              control={form.control}
              render={({ field, fieldState }) => (
                <PersonaTimezoneField
                  value={field.value}
                  onChange={field.onChange}
                  workspaceZone={workspace.timezone}
                  error={fieldState.error?.message}
                  className="sm:w-80"
                />
              )}
            />
          </Stack>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="Accounts"
        description={`The TikTok and Instagram accounts ${persona.shortName ?? persona.name} posts from. Add them by handle or profile link.`}
      >
        <SettingsCard>
          {/* 20px above and below, like the other cards (rows add their own 14px). */}
          <div className="pt-1.5 pb-5">
            <PersonaAccounts workspaceId={workspaceId} persona={persona} canEdit />
          </div>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="Posts"
        description={`Everything found on ${persona.shortName ?? persona.name}'s accounts, with its numbers on each platform as last synced. Sort a column to see what works best.`}
      >
        <SettingsCard>
          <PersonaPosts
            workspaceId={workspaceId}
            personaId={persona.id}
            name={persona.shortName ?? persona.name}
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="Reference look"
        description={`Photos of ${persona.shortName ?? persona.name} from five angles. When a flow generates images of them, they keep the same face, hair and body in every post. Use real or AI-made photos of the same person.`}
      >
        <SettingsCard>
          <div className="py-5">
            <ReferenceLook
              workspaceId={workspaceId}
              persona={persona}
              canEdit
              uploadsEnabled={workspace.uploadsEnabled}
            />
          </div>
        </SettingsCard>
      </SettingsSection>
    </SettingsPageLayout>
  );
}
